/**
 * SectionDirectorsPanel
 *
 * Panel de la pestaña "Dirección" en Configuración del club.
 * Reemplaza la card simple de "Transferir dirección" con:
 *  - Card "Director general" (con TransferDirectorshipModal).
 *  - Card "Directores por sección": estado por cada sección activa, con
 *    botones [Asignar / Cambiar / Quitar].
 *  - Card "Activar / desactivar secciones": toggles, sólo para director
 *    general o admin (no para director de sección).
 *
 * Edge cases manejados:
 *  - Sección inactiva → no se permite asignar director hasta activarla.
 *  - Desactivar sección con director asignado → confirmación, CF revoca el RA.
 *  - Desactivar última sección activa → bloqueado (mínimo 1 activa).
 *  - Director de sección NO ve el card de toggles (sólo lectura del general).
 */

import { useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Power,
  Shield,
  ShieldCheck,
  UserCog,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
  ModalFooter,
} from "@/components/ui";
import { functions } from "@/lib/firebase";
import { useClub } from "@/hooks/useOrganization";
import { useMembersByClub } from "@/hooks/usePersons";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { CLUB_TYPE_LABELS } from "@/types";
import { Can } from "@/components/auth/Can";
import type { Club, ClubType } from "@/types";
import TransferDirectorshipModal from "./TransferDirectorshipModal";
import AssignSectionDirectorDialog from "./AssignSectionDirectorDialog";
import {
  SECTION_GRADIENT_CLASSES,
  SECTION_ORDER,
} from "@/lib/constants/sections";

const ALL_SECTIONS: ClubType[] = SECTION_ORDER;

const SECTION_COLORS = SECTION_GRADIENT_CLASSES;

interface Props {
  clubId: string;
}

export default function SectionDirectorsPanel({ clubId }: Props) {
  const { profile } = useAuthStore();
  const {
    isClubDirector,
    isUnionAdmin,
    isAssociationAdmin,
    directorOfSection,
  } = usePermissions();
  const { data: club, isLoading: loadingClub } = useClub(clubId);
  const { data: members } = useMembersByClub(clubId);

  // Permisos: el director de SECCIÓN solo ve el panel en lectura (no admin).
  const canManage =
    isUnionAdmin ||
    isAssociationAdmin ||
    (isClubDirector && !directorOfSection);

  const [transferOpen, setTransferOpen] = useState(false);
  const [assignDialog, setAssignDialog] = useState<ClubType | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ClubType | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<ClubType | null>(
    null,
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sections = useMemo(() => {
    const map = (club as Club | undefined)?.sections;
    return ALL_SECTIONS.map((s) => ({
      key: s,
      cfg: map?.[s] ?? { active: false },
    }));
  }, [club]);

  const activeCount = sections.filter((s) => s.cfg.active).length;

  const memberCountBySection = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of members ?? []) {
      const sec = m.staffSection ?? m.section;
      if (!sec) continue;
      counts[sec] = (counts[sec] ?? 0) + 1;
    }
    return counts;
  }, [members]);

  const callRevoke = async (section: ClubType) => {
    setBusy(`revoke-${section}`);
    setError(null);
    try {
      const fn = httpsCallable<
        { clubId: string; section: ClubType },
        { success: boolean }
      >(functions, "revokeSectionDirector");
      await fn({ clubId, section });
      setConfirmRevoke(null);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "No se pudo quitar al director.",
      );
    } finally {
      setBusy(null);
    }
  };

  const callSetActive = async (section: ClubType, active: boolean) => {
    setBusy(`toggle-${section}`);
    setError(null);
    try {
      const fn = httpsCallable<
        { clubId: string; section: ClubType; active: boolean },
        { success: boolean }
      >(functions, "setSectionActive");
      await fn({ clubId, section, active });
      setConfirmDeactivate(null);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "No se pudo cambiar el estado.",
      );
    } finally {
      setBusy(null);
    }
  };

  if (loadingClub || !club) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="py-10 flex items-center justify-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Cargando información del club…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* === Director general === */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-rose-600" />
            Director general del club
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 p-4 bg-gray-50">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                Director actual
              </p>
              <p className="mt-1 text-base font-semibold text-gray-900">
                {club.directorName || profile?.displayName || "—"}
              </p>
              {club.directorId && (
                <p className="text-xs text-gray-500 mt-0.5">
                  ID: {club.directorId}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Tiene visibilidad completa de todos los tipos de club.
              </p>
            </div>

            {canManage && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 space-y-3">
                <div className="flex items-center gap-2 text-rose-700 font-semibold">
                  <Shield className="w-4 h-4" />
                  Transferir la dirección general
                </div>
                <p className="text-sm text-rose-700/90">
                  Esta acción traspasa el rol de director general del club a
                  otro miembro con cuenta vinculada. Tras confirmar, dejarás de
                  ser director y quedarás como miembro regular.
                </p>
                <Button
                  onClick={() => setTransferOpen(true)}
                  className="bg-gradient-to-r from-rose-500 to-rose-600"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Transferir dirección del club
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* === Directores por sección === */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Directores por tipo de club
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {sections.map(({ key, cfg }) => {
              const label = CLUB_TYPE_LABELS[key];
              const count = memberCountBySection[key] ?? 0;
              const isActive = cfg.active;
              const hasDirector = !!cfg.directorId;
              return (
                <div
                  key={key}
                  className={`rounded-xl border p-4 transition-all ${
                    isActive
                      ? "border-gray-200 bg-white"
                      : "border-gray-200 bg-gray-50 opacity-75"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-xl bg-gradient-to-br ${
                          SECTION_COLORS[key]
                        } flex items-center justify-center text-white shadow-sm`}
                      >
                        <Shield className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{label}</p>
                        {!isActive ? (
                          <p className="text-xs text-gray-500 mt-0.5">
                            No abierto
                          </p>
                        ) : hasDirector ? (
                          <p className="text-sm text-gray-700 mt-0.5">
                            <span className="text-gray-500">Director:</span>{" "}
                            <span className="font-medium">
                              {/* Nunca el id crudo: cuando faltaba el nombre,
                                  al admin le aparecía "SUFUNIOZtvPbw6z1cFTV"
                                  como si fuera una persona (4 clubes así el
                                  2026-08-11). Sin nombre se dice que falta. */}
                              {cfg.directorName || "Asignado (falta el nombre)"}
                            </span>
                          </p>
                        ) : (
                          <p className="text-sm text-amber-700 mt-0.5">
                            Sin director asignado
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-0.5">
                          {count} miembro{count === 1 ? "" : "s"}
                        </p>
                      </div>
                    </div>

                    {canManage && isActive && (
                      <div className="flex flex-wrap gap-2">
                        <Can permission="roles.write" clubId={clubId}>
                          <Button
                            size="sm"
                            variant={hasDirector ? "outline" : "primary"}
                            onClick={() => setAssignDialog(key)}
                            disabled={busy !== null}
                          >
                            {hasDirector ? (
                              <>
                                <UserCog className="w-4 h-4 mr-1.5" />
                                Cambiar
                              </>
                            ) : (
                              <>
                                <UserPlus className="w-4 h-4 mr-1.5" />
                                Asignar
                              </>
                            )}
                          </Button>
                          {hasDirector && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setConfirmRevoke(key)}
                              disabled={busy !== null}
                              className="text-rose-600 hover:bg-rose-50"
                            >
                              <UserMinus className="w-4 h-4 mr-1.5" />
                              Quitar
                            </Button>
                          )}
                        </Can>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {error && (
            <div className="mt-4 text-sm rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* === Activar / desactivar secciones === */}
      {canManage && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Power className="w-5 h-5 text-amber-600" />
              Tipos de club abiertos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Abre solo los tipos de club que tu club opera. Un tipo cerrado no
              podrá tener miembros nuevos ni eventos asignados, y se ocultará en
              la mayoría de las vistas.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {sections.map(({ key, cfg }) => {
                const label = CLUB_TYPE_LABELS[key];
                const isActive = cfg.active;
                const wouldBeLastActive = isActive && activeCount <= 1;
                const willRevokeDirector = isActive && !!cfg.directorId;
                const memberCount = memberCountBySection[key] ?? 0;
                const handleClick = () => {
                  if (!isActive) {
                    callSetActive(key, true);
                  } else if (willRevokeDirector || memberCount > 0) {
                    setConfirmDeactivate(key);
                  } else {
                    callSetActive(key, false);
                  }
                };
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={handleClick}
                    disabled={busy !== null || wouldBeLastActive}
                    className={`text-left rounded-xl border-2 p-3 transition-all ${
                      isActive
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-gray-200 bg-white hover:border-purple-300"
                    } ${
                      wouldBeLastActive
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">
                        {label}
                      </span>
                      {isActive ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <Power className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <p className="text-xs mt-1 text-gray-600">
                      {isActive ? "Activa" : "Inactiva"}
                    </p>
                    {wouldBeLastActive && (
                      <p className="text-[11px] text-amber-700 mt-1">
                        Debe quedar al menos 1 tipo de club abierto.
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      {transferOpen && (
        <TransferDirectorshipModal
          isOpen={transferOpen}
          onClose={() => setTransferOpen(false)}
          clubId={clubId}
          clubName={club.name}
          currentDirectorUserId={profile?.id}
        />
      )}

      {assignDialog && (
        <AssignSectionDirectorDialog
          isOpen={!!assignDialog}
          onClose={() => setAssignDialog(null)}
          clubId={clubId}
          clubName={club.name}
          section={assignDialog}
          generalDirectorUserId={club.directorId}
          currentSectionDirectorPersonId={
            (club.sections?.[assignDialog]?.directorId as string) || undefined
          }
        />
      )}

      {/* Confirm revoke */}
      <Modal
        isOpen={!!confirmRevoke}
        onClose={() => !busy && setConfirmRevoke(null)}
        title="Quitar director"
        size="sm"
      >
        <div className="space-y-3 text-sm text-gray-700">
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p>
              {confirmRevoke && (
                <>
                  <b>{CLUB_TYPE_LABELS[confirmRevoke]}</b> quedará sin director.
                  El director actual perderá acceso restringido y permanecerá
                  como miembro del club.
                </>
              )}
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => setConfirmRevoke(null)}
            disabled={busy !== null}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => confirmRevoke && callRevoke(confirmRevoke)}
            disabled={busy !== null}
            className="bg-rose-600 hover:bg-rose-700"
          >
            {busy?.startsWith("revoke") ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Quitando…
              </>
            ) : (
              "Quitar director"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirm deactivate */}
      <Modal
        isOpen={!!confirmDeactivate}
        onClose={() => !busy && setConfirmDeactivate(null)}
        title="Cerrar tipo de club"
        size="sm"
      >
        <div className="space-y-3 text-sm text-gray-700">
          {confirmDeactivate && (
            <>
              <p>
                Vas a cerrar <b>{CLUB_TYPE_LABELS[confirmDeactivate]}</b>.
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                {!!club.sections?.[confirmDeactivate]?.directorId && (
                  <li>
                    Se revocará el rol del director actual (queda como miembro
                    regular).
                  </li>
                )}
                {(memberCountBySection[confirmDeactivate] ?? 0) > 0 && (
                  <li>
                    Tiene{" "}
                    <b>{memberCountBySection[confirmDeactivate]} miembro(s)</b>{" "}
                    asignados. Se mantienen, pero no podrás añadir nuevos
                    miembros mientras esté inactiva.
                  </li>
                )}
                <li>Puedes volver a activarla cuando quieras.</li>
              </ul>
            </>
          )}
        </div>
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => setConfirmDeactivate(null)}
            disabled={busy !== null}
          >
            Cancelar
          </Button>
          <Button
            onClick={() =>
              confirmDeactivate && callSetActive(confirmDeactivate, false)
            }
            disabled={busy !== null}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {busy?.startsWith("toggle") ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Desactivando…
              </>
            ) : (
              "Cerrar tipo de club"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
